import { useState } from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useMeters, useForecast } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  Spinner,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function ForecastPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");

  const { data, isLoading } = useForecast(meterId || meters[0]);

  const histData = (data?.historical ?? []).map((d) => ({
    ts: fmt.dt(d.timestamp),
    value: d.value,
    type: "actual",
    lower: undefined,
    upper: undefined,
    predicted: undefined,
  }));

  const forecastData = (data?.forecast ?? []).map((d) => ({
    ts: fmt.dt(d.timestamp),
    predicted: d.predicted_kwh,
    lower: d.lower_bound,
    upper: d.upper_bound,
    value: undefined,
    type: "forecast",
  }));

  const chartData = [...histData, ...forecastData];
  const splitIndex = histData.length;

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="24-Hour Load Forecast"
          subtitle="Rolling mean baseline with confidence interval"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
        </PageHeader>

        <div className="ra-card mb-4 text-sm text-slate-400 leading-relaxed">
          <span className="text-blue-500 font-semibold">Methodology: </span>
          7-day rolling mean baseline with linear trend correction from the last
          12 hours. Confidence band = ±1.5× rolling standard deviation. With a
          single bulb connected, the forecast will appear flat — this is correct
          and expected. The model scales automatically when real loads are
          added.
        </div>

        <ChartCard
          title="Historical + 24h Forecast"
          subtitle="Shaded band = prediction interval"
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.grid}
                />
                <XAxis
                  dataKey="ts"
                  tick={{ fill: "#64748B", fontSize: 10 }}
                  interval={Math.floor(chartData.length / 12)}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0]?.payload;
                    return (
                      <div className="ra-tooltip">
                        <p className="text-slate-400 mb-1">{label}</p>
                        {p.value != null && (
                          <p className="text-blue-500">
                            Actual: {p.value.toFixed(2)} W
                          </p>
                        )}
                        {p.predicted != null && (
                          <p className="text-emerald-600">
                            Forecast: {p.predicted.toFixed(2)} W
                          </p>
                        )}
                        {p.lower != null && (
                          <p className="text-slate-500">
                            Range: {p.lower.toFixed(1)} – {p.upper.toFixed(1)}
                          </p>
                        )}
                      </div>
                    );
                  }}
                />
                {/* Confidence band */}
                <Area
                  dataKey="upper"
                  stroke="none"
                  fill={CHART_COLORS.emerald}
                  fillOpacity={0.08}
                  name="Upper"
                />
                <Area
                  dataKey="lower"
                  stroke="none"
                  fill="#ffffff"
                  fillOpacity={1}
                  name="Lower"
                />
                {/* Historical */}
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={CHART_COLORS.blue}
                  dot={false}
                  strokeWidth={2}
                  name="Actual"
                />
                {/* Forecast */}
                <Line
                  type="monotone"
                  dataKey="predicted"
                  stroke={CHART_COLORS.emerald}
                  dot={false}
                  strokeWidth={2}
                  strokeDasharray="6 3"
                  name="Forecast"
                />
                {/* Boundary */}
                {splitIndex > 0 && (
                  <ReferenceLine
                    x={chartData[splitIndex - 1]?.ts}
                    stroke={CHART_COLORS.amber}
                    strokeDasharray="4 2"
                    label={{ value: "Now", fill: "#F59E0B", fontSize: 10 }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </Layout>
  );
}
